<template>
  <div
    v-if="catalogData"
    :class="{
      'product-family-head-with-facets-fi': true,
      'product-family-head-with-facets-fi--no-facets': !loading && !headFacets?.length,
    }"
  >
    <div
      v-if="marketingClaim"
      class="product-family-head-with-facets-fi__marketing-claim"
    >
      {{ marketingClaim }}
    </div>
    <div class="product-family-head-with-facets-fi__data">
      <h1
        v-if="familyName"
        class="product-family-head-with-facets-fi__name"
      >
        {{ familyName }}
      </h1>

      <div class="product-family-head-with-facets-fi__links">
        <link-fi
          class="product-family-head-with-facets-fi__link"
          href="#"
          :size="18"
          data-test="product-variants-link"
          @click.stop.prevent="$emit('scroll-to-variants')"
        >
          {{ $dict.get('product-Family.Anchor-Link-Text') }}
        </link-fi>

        <link-fi
          v-if="mediaDownloadLink && fields.mediaDownloadLabel?.value"
          class="product-family-head-with-facets-fi__link"
          rel="nofollow"
          href="#"
          :size="18"
          @click.stop.prevent="openMediaDownloadDialog"
        >
          {{ fields.mediaDownloadLabel.value }}
        </link-fi>
      </div>

      <system-partner-hint-fi
        v-if="showHint"
        class="product-family-head-with-facets-fi__system-partner-hint"
      />

      <div class="product-family-head-with-facets-fi__slider">
        <catalog-slider-fi
          :catalog-data="catalogData"
          :item-name="familyName"
        />
      </div>
    </div>

    <!-- eslint-disable vue/no-v-html -->
    <div
      v-if="description"
      class="product-family-head-with-facets-fi__description richtext-fi"
      v-html="description"
    />
    <!-- eslint-enable vue/no-v-html -->

    <div
      v-if="loading || headFacets.length > 0"
      class="product-family-head-with-facets-fi__facets-container"
    >
      <loading-indicator
        v-if="loading"
        variant="spinnerLocal"
      />

      <div
        v-if="headFacets.length > 0"
        class="product-family-head-with-facets-fi__facets"
      >
        <div class="product-family-head-with-facets-fi__facets__head">
          <div class="product-family-head-with-facets-fi__facets__title">
            {{ $dict.get('product-Family.Facets.Title') }}
          </div>
          <div
            v-if="activeFacets.length"
            class="product-family-head-with-facets-fi__facets__clear"
            @click="clearFacets"
          >
            {{ $dict.get('product-Family.Facets.Clear-All') }}
            <icon-fi
              icon="load-loop"
              class="fi--my-12 fi--mr-12"
            />
          </div>
        </div>
        <div class="product-family-head-with-facets-fi__facets__wrapper">
          <product-family-head-facet-fi
            v-for="facet in headFacets"
            :key="facet.associatedFieldName"
            :facet="facet"
            @change-facet="changeFacet"
          />
        </div>

        <button-fi
          v-if="totalHits > 1"
          class="button-fi__primary product-family-head-with-facets-fi__facets__button button-fi--large button-fi__xs-streched"
          @click="$emit('scroll-to-variants')"
        >
          {{ $dict.get('product-Family.Facets.Scroll-To-Variants', [String(totalHits)]) }}
        </button-fi>
      </div>

      <product-family-head-variant-fi
        v-if="variant"
        :key="variant.ID"
        :variant="variant"
      />
    </div>
  </div>

  <media-download-dialog-fi
    ref="mediaDownloadDialog"
    :media-download-link="mediaDownloadLink"
    :file-name="familyName"
    :fields="fields || null"
  />
</template>

<script setup lang="ts">
import { ButtonFi, IconFi, LinkFi, LoadingIndicator } from 'atoms';
import { computed, toRefs, useTemplateRef } from 'vue';

import CatalogSliderFi from '@/components/catalog-slider-fi/CatalogSliderFi.vue';
import MediaDownloadDialogFi from '@/components/product-detail/media-download-dialog-fi/MediaDownloadDialogFi.vue';
import SystemPartnerHintFi from '@/components/system-partner-hint-fi/SystemPartnerHintFi.vue';
import { useCatalogTextList } from '@/composables/catalogTextList';
import { useFields } from '@/composables/fields';
import { useSystemPartner } from '@/composables/systemPartner';
import { useProductFamilyStore } from '@/store/pinia/product-family';

import ProductFamilyHeadFacetFi from './product-family-head-facet-fi/ProductFamilyHeadFacetFi.vue';
import ProductFamilyHeadVariantFi from './product-family-head-variant-fi/ProductFamilyHeadVariantFi.vue';

defineOptions({
  name: 'ProductFamilyHeadWithFacetsFi',
});

const fields = useFields('ProductFamilyPageFiFields');

const { showSystemPartnerHint } = useSystemPartner();

const { textListValue } = useCatalogTextList();
const { headFacets, activeFacets, loading, totalHits, familyProducts } =
  toRefs(useProductFamilyStore());

const props = defineProps<{
  catalogData: any;
  mediaDownloadLink?: string;
}>();

const emits = defineEmits<{
  'scroll-to-variants': [value: void];
  'change-facet': [value: any];
  'clear-facets': [value: void];
}>();

const mediaDownloadDialogRef = useTemplateRef('mediaDownloadDialog');

const marketingClaim = computed(
  () =>
    textListValue(props.catalogData?.TextList, 'KAT_POS') ||
    textListValue(props.catalogData?.TextList, 'DOKBSTMARKCLAIM') ||
    '',
);

const familyName = computed(
  () =>
    textListValue(props.catalogData?.TextList, 'BEZEICHNUNG') ||
    textListValue(props.catalogData?.TextList, 'ARTIKELLANG') ||
    '',
);

const description = computed(() => props.catalogData?.description);

const showHint = computed(() => showSystemPartnerHint(props.catalogData));

const openMediaDownloadDialog = () => {
  mediaDownloadDialogRef.value?.show();
};

const variant: any = computed(() =>
  familyProducts.value?.length === 1 ? familyProducts.value[0] : null,
);

const clearFacets = () => {
  activeFacets.value = [];
  emits('clear-facets');
};

const changeFacet = (option: any) => {
  if (option.disabled) {
    return;
  }

  emits('change-facet', {
    value: !option.selected,
    element: {
      associatedFieldName: option.associatedFieldName,
      name: option.text,
      selected: !option.selected,
    },
  });
};
</script>

<style scoped src="./product-family-head-with-facets-fi" />
